import { useEffect, useRef } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Check } from "lucide-react";
import CameraArt from "@/components/album/CameraArt";
import { drawThumb } from "@/lib/photoFilter";
import { FILTERS } from "@/config/album";
import { cn } from "@/lib/utils";

// Pratinjau satu nuansa memakai foto cover pemilik acara sendiri
const Thumb = ({ src, id, max = 260, className }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!src) return undefined;
    let live = true;
    const img = new Image();
    img.onload = () => {
      if (live && ref.current) drawThumb(ref.current, img, id, max);
    };
    img.src = src;
    return () => {
      live = false;
    };
  }, [src, id, max]);
  return <canvas ref={ref} className={cn("bg-[#E8E3D9] object-cover", className)} />;
};

export default function FilterStep({ cover, value, onChange }) {
  const reduce = useReducedMotion();
  const cur = FILTERS.find((f) => f.id === value) || FILTERS[0];
  return (
    <div>
      {/* kamera "mencetak" foto sesuai nuansa yang dipilih */}
      <div className="relative mx-auto w-full max-w-[19rem]">
        <CameraArt className="relative z-10 mx-auto w-60 drop-shadow-[0_14px_18px_rgba(17,17,17,0.18)]" />
        <div className="relative z-0 -mt-7 flex justify-center">
          <AnimatePresence mode="wait">
            <motion.figure
              key={cur.id}
              initial={reduce ? false : { y: -90, opacity: 0, rotate: -4 }}
              animate={{ y: 0, opacity: 1, rotate: 1.5 }}
              exit={reduce ? undefined : { y: -60, opacity: 0 }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className="w-44 bg-white p-2 pb-7 shadow-[0_18px_30px_-12px_rgba(17,17,17,0.35)] ring-1 ring-[#E8E3D9]"
            >
              <Thumb src={cover} id={cur.id} max={360} className="aspect-[4/5] w-full" />
              <figcaption className="mt-3 text-center font-serif text-lg italic text-[#111111]">{cur.label}</figcaption>
            </motion.figure>
          </AnimatePresence>
        </div>
      </div>

      <p className="mt-6 text-center font-sans text-sm text-[#5C5C5C]">{cur.desc}</p>

      <div className="mt-6 grid grid-cols-3 gap-3">
        {FILTERS.map((o) => {
          const on = o.id === value;
          return (
            <button key={o.id} type="button" aria-pressed={on} onClick={() => onChange(o.id)} className="group text-center outline-none">
              <span
                className={cn(
                  "relative block overflow-hidden transition-all duration-300",
                  on
                    ? "ring-2 ring-[#C6A56B] ring-offset-2 ring-offset-[#F9F8F6]"
                    : "ring-1 ring-[#E8E3D9] group-hover:ring-[#C6A56B]/60 group-focus-visible:ring-[#C6A56B]"
                )}
              >
                <Thumb src={cover} id={o.id} max={140} className="aspect-square w-full" />
                {on && (
                  <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center bg-[#C6A56B] text-[#111111]">
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </span>
                )}
              </span>
              <span className={cn("mt-2 block font-sans text-xs", on ? "text-[#111111]" : "text-[#8A8A8A]")}>{o.label}</span>
            </button>
          );
        })}
      </div>

      <p className="mt-6 font-sans text-xs leading-relaxed text-[#8A8A8A]">
        Foto asli tamu tidak disimpan. Yang tersimpan di album adalah foto dengan nuansa yang kamu pilih, jadi pilih Asli kalau ingin foto apa adanya.
      </p>
    </div>
  );
}
